import { MapPin } from 'lucide-react';

interface LocationDisclosureModalProps {
  open: boolean;
  /**
   * Called when the user taps "Continue". The caller MUST then proceed to the
   * OS permission request — this disclosure never offers a way to opt out of the
   * prompt itself (Apple Guideline 5.1.1(iv)). The OS dialog is where the user
   * grants or denies access.
   */
  onContinue: () => void;
}

/**
 * Prominent in-app disclosure shown once before the OS location-permission
 * prompt. Required by Google Play policy (prominent disclosure for background
 * location) and compliant with Apple Guideline 5.1.1(iv):
 *   - the only action is a "Continue" button (no "Allow"/"Deny" wording),
 *   - there is no exit/dismiss affordance, so the user always proceeds to the
 *     system permission request after reading the message.
 */
function LocationDisclosureModal({ open, onContinue }: LocationDisclosureModalProps) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-blue-100 rounded-full p-2">
            <MapPin className="h-6 w-6 text-blue-600" />
          </div>
          <h2 className="text-lg font-semibold text-neutral-900">Location Access</h2>
        </div>

        <p className="text-sm text-neutral-700 mb-3">
          <strong>FactreeTech</strong> uses your device location to support the work features you
          use, including when the app is running in the background during active sessions.
        </p>

        <ul className="text-sm text-neutral-600 space-y-2 mb-4 list-disc list-inside">
          <li>Clock in and clock out — a single location fix is attached to your timesheet.</li>
          <li>Sprays and farm jobs — route waypoints are recorded while the session is active for coverage mapping and compliance.</li>
          <li>Hand work such as pruning or picking — while you are on the job and the app is open, one position about every 10 minutes records which block your time was spent in. It is not a live map of you.</li>
          <li>Weather and map features — your location centres the map or picks the nearest forecast.</li>
          <li>Location is uploaded to your organisation's account on our secure server and is not shared with third parties.</li>
          <li>Background tracking only runs while you have a spray, farm job, or equipment session active, and stops when you end it.</li>
        </ul>

        <p className="text-xs text-neutral-400 mb-5">
          After you continue, your device will ask for permission. You can choose to allow or deny
          it there, and you can change it at any time in your device Settings.
        </p>

        <button
          onClick={onContinue}
          className="w-full py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
        >
          Continue
        </button>
      </div>
    </div>
  );
}

export default LocationDisclosureModal;
